// Simulador: cuánto pagaría la venta siguiente de un vendedor modelo y cómo cerraría su semana con
// las reglas que se prendan. No toca producción. Mockup: portal-admin-vl-comisiones.html
import { useState } from 'react';
import { toast } from 'sonner';
import type { RetailCommission } from '@fiado/type-kit';
import { CommissionRuleStatusEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionRuleStatusEnum';
import type { ProductCategoryEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductCategoryEnum';
import type { ProductTierEnum } from '@fiado/type-kit/bin/retailCatalog/enums/ProductTierEnum';
import { userMessageOf } from '@/api/errors';
import { Button } from '@/kit';
import { formatMoneyCents } from '@/shared/lib/format/money';
import { useSimulateRules } from '../../api/commissionQueries';
import type { CommissionDirectory } from '../../api/useCommissionDirectory';
import { localToday } from '../../lib/payoutPeriods';
import { isRealDate } from '../../lib/ruleForm';
import { describeCondition, factor, STATUS_LABEL } from '../../lib/ruleText';
import { FIELD, LABEL, SECTION_TITLE } from '../fieldStyles';

type Rule = RetailCommission.CommissionRule;

/** Los vendedores modelo del mock, como punto de partida editable. */
const PROFILES = [
    { id: 'MOD-PROM', name: 'Vendedor promedio', units: 7, placementPesos: 28_000, skus: 2 },
    { id: 'MOD-TOP', name: 'Vendedor top', units: 18, placementPesos: 95_000, skus: 5 },
    { id: 'MOD-BAJO', name: 'Vendedor bajo', units: 2, placementPesos: 6_200, skus: 1 },
    {
        id: 'MOD-NUEVO',
        name: 'Vendedor nuevo (primer mes)',
        units: 4,
        placementPesos: 12_500,
        skus: 1,
    },
    {
        id: 'MOD-SUBDIST',
        name: 'Vendedor de subdistribuidor',
        units: 5,
        placementPesos: 18_000,
        skus: 2,
    },
    {
        id: 'MOD-ALTA',
        name: 'Especialista en gama alta',
        units: 4,
        placementPesos: 52_000,
        skus: 2,
    },
] as const;

/** Topes del simulador en el motor. */
const MAX_RULES = 50;
const MAX_UNITS = 500;
const MAX_PLACEMENT_PESOS = 10_000_000;

const SKIP_LABEL: Record<`${RetailCommission.CommissionSimulationSkipEnum}`, string> = {
    NOT_IN_EFFECT: 'fuera de vigencia en esa fecha',
    OUT_OF_SCOPE: 'el producto o la tienda no caen en su alcance',
    CONDITION_NOT_MET: 'no cumple su condición en esa posición',
    NO_DATA: 'el simulador no tiene el NPI M30',
};

/** Entre 0 y el tope, entero si se pide; un campo vacío vale 0. */
const inRange = (text: string, max: number, integer: boolean) => {
    const value = Number(text);
    return (
        Number.isFinite(value) &&
        value >= 0 &&
        value <= max &&
        (!integer || Number.isInteger(value))
    );
};

interface RuleSimulatorProps {
    rules: Rule[];
    directory: CommissionDirectory;
    /** La regla que mandó "Correr simulador": arranca sola. */
    isolate: string | null;
}

export function RuleSimulator({ rules, directory, isolate }: RuleSimulatorProps) {
    const [profileId, setProfileId] = useState<string>(PROFILES[0].id);
    const [units, setUnits] = useState(String(PROFILES[0].units));
    const [placement, setPlacement] = useState(String(PROFILES[0].placementPesos));
    const [skus, setSkus] = useState(String(PROFILES[0].skus));
    const [sku, setSku] = useState('');
    const [storeId, setStoreId] = useState('');
    const [date, setDate] = useState(() => localToday());
    const [on, setOn] = useState<ReadonlySet<string>>(
        () =>
            new Set(
                isolate
                    ? [isolate]
                    : rules
                          .filter((r) => r.status === CommissionRuleStatusEnum.ACTIVE)
                          .map((r) => r.ruleId),
            ),
    );
    const simulate = useSimulateRules();
    // Solo cuentan las reglas que siguen en el catálogo: una borrada no viaja ni suma al tope.
    const selected = rules.filter((r) => on.has(r.ruleId)).map((r) => r.ruleId);
    const result = simulate.data;

    const selectedSku = sku !== '' ? sku : (directory.products.at(0)?.sku ?? '');
    const product = directory.product(selectedSku);
    const canRun =
        product !== undefined &&
        selected.length > 0 &&
        selected.length <= MAX_RULES &&
        inRange(units, MAX_UNITS, true) &&
        inRange(placement, MAX_PLACEMENT_PESOS, false) &&
        inRange(skus, MAX_UNITS, true) &&
        (date === '' || isRealDate(date));

    const pickProfile = (id: string) => {
        setProfileId(id);
        const profile = PROFILES.find((p) => p.id === id);
        if (!profile) return;
        setUnits(String(profile.units));
        setPlacement(String(profile.placementPesos));
        setSkus(String(profile.skus));
    };

    const toggleRule = (ruleId: string) => {
        setOn((current) => {
            const next = new Set(current);
            if (next.has(ruleId)) next.delete(ruleId);
            else next.add(ruleId);
            return next;
        });
    };

    const run = () => {
        if (!product) return;
        simulate.mutate(
            {
                // Con la lista vacía el motor simula el plan vigente: aquí siempre va la selección.
                ruleIds: selected,
                profile: {
                    unitsInWeek: Number(units),
                    placementCents: Math.round(Number(placement) * 100),
                    distinctSkus: Number(skus),
                },
                product: {
                    sku: product.sku,
                    category: (product.category ?? null) as ProductCategoryEnum | null,
                    tier: product.tier as ProductTierEnum | null,
                    netCents: product.basePriceCents,
                },
                storeId: storeId === '' ? null : storeId,
                date: date === '' ? null : date,
            },
            {
                onError: (error) => {
                    toast.error(userMessageOf(error));
                },
            },
        );
    };

    return (
        <div className="grid min-h-0 flex-1 grid-cols-[340px_1fr] overflow-hidden">
            <aside className="flex min-h-0 flex-col gap-4 overflow-auto border-r border-border p-4">
                <section>
                    <label htmlFor="sim-profile" className={SECTION_TITLE}>
                        1 · Vendedor modelo
                    </label>
                    <select
                        id="sim-profile"
                        className={FIELD}
                        value={profileId}
                        onChange={(event) => {
                            pickProfile(event.target.value);
                        }}
                    >
                        {PROFILES.map((profile) => (
                            <option key={profile.id} value={profile.id}>
                                {profile.name}
                            </option>
                        ))}
                    </select>
                    <div className="mt-2 grid grid-cols-3 gap-2">
                        <div>
                            <label htmlFor="sim-units" className={LABEL}>
                                Piezas semana
                            </label>
                            <input
                                id="sim-units"
                                type="number"
                                min={0}
                                max={MAX_UNITS}
                                step={1}
                                className={FIELD}
                                value={units}
                                onChange={(event) => {
                                    setUnits(event.target.value);
                                }}
                            />
                        </div>
                        <div>
                            <label htmlFor="sim-placement" className={LABEL}>
                                Colocación $
                            </label>
                            <input
                                id="sim-placement"
                                type="number"
                                min={0}
                                max={MAX_PLACEMENT_PESOS}
                                step={100}
                                className={FIELD}
                                value={placement}
                                onChange={(event) => {
                                    setPlacement(event.target.value);
                                }}
                            />
                        </div>
                        <div>
                            <label htmlFor="sim-skus" className={LABEL}>
                                Modelos
                            </label>
                            <input
                                id="sim-skus"
                                type="number"
                                min={0}
                                max={MAX_UNITS}
                                step={1}
                                className={FIELD}
                                value={skus}
                                onChange={(event) => {
                                    setSkus(event.target.value);
                                }}
                            />
                        </div>
                    </div>
                    <p className="mt-1 text-[10.5px] text-muted-foreground">
                        Colocación y modelos se toman tal cual, sin sumar la venta simulada: con
                        condiciones de monto o de modelos el resultado es aproximado.
                    </p>
                </section>

                <section className="flex flex-col gap-2">
                    <span className={SECTION_TITLE}>2 · Venta de referencia</span>
                    <div>
                        <label htmlFor="sim-product" className={LABEL}>
                            Producto
                        </label>
                        <select
                            id="sim-product"
                            className={FIELD}
                            value={selectedSku}
                            onChange={(event) => {
                                setSku(event.target.value);
                            }}
                        >
                            {directory.products.map((p) => (
                                <option key={p.sku} value={p.sku}>
                                    {`${`${p.brand ?? ''} ${p.model ?? ''}`.trim() || p.sku} · ${formatMoneyCents(p.basePriceCents)}`}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label htmlFor="sim-store" className={LABEL}>
                                Tienda
                            </label>
                            <select
                                id="sim-store"
                                className={FIELD}
                                value={storeId}
                                onChange={(event) => {
                                    setStoreId(event.target.value);
                                }}
                            >
                                <option value="">Sin tienda</option>
                                {directory.stores.map((s) => (
                                    <option key={s.storeId} value={s.storeId}>
                                        {s.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label htmlFor="sim-date" className={LABEL}>
                                Fecha
                            </label>
                            <input
                                id="sim-date"
                                type="date"
                                className={FIELD}
                                value={date}
                                onChange={(event) => {
                                    setDate(event.target.value);
                                }}
                            />
                        </div>
                    </div>
                    {storeId === '' && (
                        <p className="text-[10.5px] text-muted-foreground">
                            Sin tienda no se evalúa el alcance por tienda.
                        </p>
                    )}
                </section>

                <section className="flex min-h-0 flex-col gap-2">
                    <span className={SECTION_TITLE}>3 · Reglas en el simulador</span>
                    <p
                        className={
                            selected.length === rules.length
                                ? 'text-[11px] text-success'
                                : 'text-[11px] text-warning'
                        }
                    >
                        Simulando con{' '}
                        <b>
                            {selected.length}/{rules.length}
                        </b>{' '}
                        reglas
                        {selected.length > MAX_RULES
                            ? ` · hasta ${String(MAX_RULES)} a la vez`
                            : ''}
                    </p>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                setOn(new Set(rules.map((r) => r.ruleId)));
                            }}
                        >
                            Prender todas
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                                setOn(new Set());
                            }}
                        >
                            Apagar todas
                        </Button>
                    </div>
                    <ul className="flex flex-col gap-1">
                        {rules.map((r) => (
                            <li key={r.ruleId} className="flex items-start gap-2 text-[11px]">
                                <input
                                    type="checkbox"
                                    id={`sim-rule-${r.ruleId}`}
                                    checked={on.has(r.ruleId)}
                                    onChange={() => {
                                        toggleRule(r.ruleId);
                                    }}
                                />
                                <label htmlFor={`sim-rule-${r.ruleId}`} className="min-w-0 flex-1">
                                    <span className="block truncate font-medium">{r.name}</span>
                                    <span className="block truncate text-[10px] text-muted-foreground">
                                        {STATUS_LABEL[r.status]} ·{' '}
                                        {describeCondition(r.condition, r.countBy)}
                                    </span>
                                </label>
                                <button
                                    type="button"
                                    className="text-[10px] font-semibold text-primary"
                                    title="Aislar esta regla (apagar todas las demás)"
                                    onClick={() => {
                                        setOn(new Set([r.ruleId]));
                                    }}
                                >
                                    SOLO
                                </button>
                            </li>
                        ))}
                    </ul>
                    <p className="text-[10.5px] italic text-muted-foreground">
                        Cada regla se simula en su última versión como si estuviera publicada,
                        aunque esté en borrador o pausada. Prender o apagar aquí no afecta
                        producción.
                    </p>
                </section>

                <Button onClick={run} disabled={!canRun} isLoading={simulate.isPending}>
                    Simular
                </Button>
            </aside>

            <section className="min-h-0 overflow-auto p-4" aria-label="Resultado de la simulación">
                {!result ? (
                    <p className="text-[12px] text-muted-foreground">
                        Elige un vendedor modelo, un producto y las reglas, y pulsa Simular.
                    </p>
                ) : (
                    <div className="grid gap-4 xl:grid-cols-[1.6fr_1fr]">
                        <section>
                            <h3 className={SECTION_TITLE}>
                                Progresión del corte · {result.progression.length} piezas
                            </h3>
                            <p className="mb-2 text-[10.5px] text-muted-foreground">
                                Supone que todas las piezas de la semana son del producto elegido.
                            </p>
                            <table className="w-full text-[11px]">
                                <thead>
                                    <tr className="text-left text-muted-foreground">
                                        <th className="py-1">#</th>
                                        <th className="py-1 text-right">Bono</th>
                                        <th className="py-1 text-right">Acumulado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.progression.map((point) => (
                                        <tr key={point.position} className="border-t border-border">
                                            <td className="py-1">{point.position}</td>
                                            <td className="py-1 text-right tabular-nums">
                                                {formatMoneyCents(point.subtotalBonusCents)}
                                            </td>
                                            <td className="py-1 text-right tabular-nums">
                                                {formatMoneyCents(point.cumulativeBonusCents)}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <dl className="mt-3 grid grid-cols-[1fr_auto] gap-x-3 gap-y-1 rounded-md border border-border p-3 text-[11px]">
                                <dt>Subtotal bonos por venta</dt>
                                <dd className="text-right tabular-nums">
                                    {formatMoneyCents(result.week.subtotalBonusCents)}
                                </dd>
                                <dt>
                                    × Multiplicador{' '}
                                    {result.week.multipliers.map((m) => m.name).join(' · ') ||
                                        'ninguno'}
                                    {result.week.multiplierBeforeCapBps !==
                                    result.week.multiplierBps
                                        ? ` (con tope; sin tope ${factor(result.week.multiplierBeforeCapBps)})`
                                        : ''}
                                </dt>
                                <dd className="text-right">{factor(result.week.multiplierBps)}</dd>
                                <dt>
                                    + Bonos umbral{' '}
                                    {result.week.thresholds.map((t) => t.name).join(' · ')}
                                </dt>
                                <dd className="text-right tabular-nums">
                                    {formatMoneyCents(result.week.thresholdBonusCents)}
                                </dd>
                                <dt className="font-semibold">Total del corte</dt>
                                <dd className="text-right font-semibold tabular-nums">
                                    {formatMoneyCents(result.week.releasedCents)}
                                </dd>
                            </dl>
                        </section>
                        <section>
                            <h3 className={SECTION_TITLE}>
                                Venta siguiente · #{result.nextSale.position}
                            </h3>
                            <p className="mb-2 text-[22px] font-semibold tabular-nums">
                                {formatMoneyCents(result.nextSale.subtotalBonusCents)}
                            </p>
                            <ul className="flex flex-col gap-1 text-[11px]">
                                {result.nextSale.rules.map((r) => (
                                    <li
                                        key={r.ruleId}
                                        className="flex justify-between gap-2 border-t border-border py-1"
                                    >
                                        <span className="min-w-0 truncate">{r.name}</span>
                                        {r.skipped ? (
                                            <span className="text-right text-muted-foreground">
                                                no paga: {SKIP_LABEL[r.skipped]}
                                            </span>
                                        ) : (
                                            <b className="tabular-nums">
                                                +{formatMoneyCents(r.amountCents)}
                                            </b>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </section>
                    </div>
                )}
            </section>
        </div>
    );
}
